import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';

import { useAuth } from '#root/hooks';
import { userService } from '#root/services';
import { AddressDocumentFields, GuveryTextField } from '#root/components';
import type { AddressDocumentValue } from '#root/components/AddressDocumentFields';
import { GuveryDialog } from '#root/ui-components';
import { DocumentType, DocumentTypeValue } from '#root/models/Shipping';
import { isValidDocNumber } from '#root/utils';

interface DialogNewAddressProps {
  createMessage: any;
  fetchUserAddress?: Function;
  open: boolean;
  onClose: (e?: MouseEvent<HTMLElement>) => void;
  departmentArray?: any[];
}

// ¿Quién recibe? "Recibo yo" marca la dirección como propia (flag_idu = 1)
const Receiver = {
  self: 'self',
  other: 'other',
} as const;

type ReceiverValue = (typeof Receiver)[keyof typeof Receiver];

interface NewAddressState {
  name: string;
  lastName: string;
  phone: string;
  docType: DocumentTypeValue;
  docNumber: string;
  address: string;
  address2: string;
  district: string;
  province: string;
  department: string;
}

type ReceiverFields = Pick<NewAddressState, 'name' | 'lastName' | 'phone' | 'docType' | 'docNumber'>;

const validateNewAddress = (newAddress: NewAddressState) => {
  const { name, lastName, phone, address, district, province, department } = newAddress;
  if (
    !name.trim() ||
    !lastName.trim() ||
    !phone.trim() ||
    !address.trim() ||
    !district.trim() ||
    !province.trim() ||
    !department
  ) {
    return false;
  }
  return isValidDocNumber(newAddress.docType, newAddress.docNumber);
};

function DialogNewAddress(props: DialogNewAddressProps) {
  const {
    createMessage,
    fetchUserAddress,
    open = false,
    onClose,
    departmentArray = [],
  } = props;
  const { usuario } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [receiver, setReceiver] = useState<ReceiverValue>(Receiver.self);
  const [isPrimary, setIsPrimary] = useState(false);

  // "Recibo yo" precarga los datos de la cuenta (todos editables). El documento solo si la cuenta
  // tiene DNI o CE: es el de quien recibe en esta dirección y nunca se escribe en la cuenta.
  const getReceiverFields = (value: ReceiverValue): ReceiverFields => {
    if (value === Receiver.other) {
      return { name: '', lastName: '', phone: '', docType: DocumentType.dni, docNumber: '' };
    }
    const accountDocType = [DocumentType.dni, DocumentType.ce].find(
      (type) => type === usuario?.tipdoc,
    );
    const accountDocNumber = String(usuario?.numdoc ?? '').trim();
    const canPrefillDoc = !!accountDocType && /^\d+$/.test(accountDocNumber);
    return {
      name: usuario?.nom ?? '',
      lastName: usuario?.ape ?? '',
      phone: usuario?.wsp ?? '',
      docType: canPrefillDoc ? accountDocType : DocumentType.dni,
      docNumber: canPrefillDoc ? accountDocNumber : '',
    };
  };

  const getInitialAddress = (): NewAddressState => ({
    ...getReceiverFields(Receiver.self),
    address: '',
    address2: '',
    district: '',
    province: '',
    department: '',
  });

  const [newAddress, setNewAddress] = useState<NewAddressState>(getInitialAddress);
  const isFormValid = validateNewAddress(newAddress);

  const handleReceiverChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as ReceiverValue;
    setReceiver(value);
    // Solo cambian los datos de quien recibe; la dirección escrita se conserva
    setNewAddress({ ...newAddress, ...getReceiverFields(value) });
  };

  const handleInputChange = (e) => {
    setNewAddress({
      ...newAddress,
      [e.target.id]: e.target.value,
    });
  };

  const handleSelectChange = (e) => {
    setNewAddress({
      ...newAddress,
      department: e.target.value,
    });
  };

  const handleDocumentChange = (value: AddressDocumentValue) => {
    setNewAddress({
      ...newAddress,
      docType: value.docType,
      docNumber: value.docNumber,
    });
  };

  const handleCheckboxChange = (e) => {
    setIsPrimary(e.target.checked);
  };

  const handleNewAddress = async () => {
    if (!isFormValid || submitting) return;
    setSubmitting(true);
    const res = await userService.addAddress({
      name: newAddress.name,
      lastName: newAddress.lastName,
      phone: newAddress.phone,
      isSelf: receiver === Receiver.self,
      documentType: newAddress.docType,
      documentNumber: newAddress.docNumber,
      address: newAddress.address,
      address2: newAddress.address2,
      district: newAddress.district,
      province: newAddress.province,
      department: newAddress.department,
      isPrimary,
    });
    setSubmitting(false);
    if (res.error) {
      // El server valida WhatsApp y departamento: el diálogo sigue abierto con lo escrito
      createMessage.error(res.message);
      return;
    }
    createMessage.success(res.message);
    fetchUserAddress?.();
    onClose();
  };

  // Formulario limpio cada vez que se abre
  useEffect(() => {
    if (open) {
      setReceiver(Receiver.self);
      setNewAddress(getInitialAddress());
      setIsPrimary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <GuveryDialog
      open={open}
      onClose={onClose}
      onConfirm={handleNewAddress}
      onConfirmDisable={!isFormValid || submitting}
      title="Agregar una nueva dirección">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">¿Quién recibe?</FormLabel>
            <RadioGroup row name="receiver" value={receiver} onChange={handleReceiverChange}>
              <FormControlLabel
                value={Receiver.self}
                control={<Radio color="primary" />}
                label="Recibo yo"
              />
              <FormControlLabel
                value={Receiver.other}
                control={<Radio color="primary" />}
                label="Otra persona"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <GuveryTextField
            required
            id="name"
            label="Nombre"
            type="text"
            value={newAddress.name}
            onChange={handleInputChange}
            rules={[{ required: true, message: 'Por favor ingresa tu nombre!' }]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <GuveryTextField
            required
            id="lastName"
            label="Apellido"
            type="text"
            value={newAddress.lastName}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <GuveryTextField
            required
            id="phone"
            label="WhatsApp de quien recibe"
            type="text"
            value={newAddress.phone}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <AddressDocumentFields
            docType={newAddress.docType}
            docNumber={newAddress.docNumber}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={12}>
          <GuveryTextField
            required
            id="address"
            label="Dirección 1"
            type="text"
            value={newAddress.address}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <GuveryTextField
            id="address2"
            label="Dirección 2 (opcional)"
            type="text"
            value={newAddress.address2}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <GuveryTextField
            required
            id="district"
            label="Distrito"
            type="text"
            value={newAddress.district}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <GuveryTextField
            required
            id="province"
            label="Provincia"
            type="text"
            value={newAddress.province}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            select
            fullWidth
            variant="outlined"
            id="department"
            label="Departamento"
            type="text"
            value={newAddress.department}
            onChange={handleSelectChange}>
            {departmentArray.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={isPrimary} onChange={handleCheckboxChange} />}
            label=" Marcar como dirección principal."
          />
        </Grid>
      </Grid>
    </GuveryDialog>
  );
}

export default DialogNewAddress;
